import { ReactNode, useCallback, useMemo } from "react";
import {
  Campaign,
  DMAutomationPlatform,
  Event,
  MassTextType,
} from "@markit/common.types";
import {
  displayAutomationTriggerLabel,
  formatDate,
  formatNumericDate,
  handlePluralString,
  isDefaultTemplateAutomations,
  isDelayedTriggerAutomation,
  isEventRelatedType,
  LessThanDate,
  relativeSendTimeLabel,
  truncateString,
} from "@markit/common.utils";
import { Colors } from "../../../utils/colors";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { CampaignDetails } from "../../../redux/slices/campaignSlice";
import { Icon } from "@iconify/react";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { useSelector } from "react-redux";
import { getEventState } from "../../../redux/slices/eventSlice";
import PresetAutomationTriggerIcon from "../../CreatorDashboard/Campaigns/Items/PresetAutomationTriggerIcon";
import EventItemImage from "../EventItem/EventItemImage";
import { SocialPlatformIconItem } from "../AutoDM/SocialPlatformIconItem";

type AutomationItemTimelineProps = {
  campaignItem: Campaign;
  campaignDetail: CampaignDetails | undefined;
  dmAutomationPlatform?: DMAutomationPlatform;
};

export const AutomationItemTimeline = ({
  campaignItem,
  campaignDetail,
  dmAutomationPlatform,
}: AutomationItemTimelineProps) => {
  const { events } = useSelector(getEventState).events;

  const styles = {
    timelineItemContainer: {
      borderRadius: 8,
      padding: 4,
    },
    timelineItemHeader: { fontSize: 10, fontWeight: 500 },
  };

  const massTexts = useMemo(
    () =>
      campaignDetail
        ? campaignDetail.massTextDetails.map((detail) => detail.massText)
        : [],
    [campaignDetail]
  );

  const foundEvent = useMemo(
    () => events.find((event) => event.id === campaignItem.eventId),
    [campaignItem.eventId, events]
  );

  const delayedTriggerAutomation = useMemo(
    () => isDelayedTriggerAutomation(campaignItem, foundEvent),
    [campaignItem, foundEvent]
  );

  const eventRelatedAutomation = useMemo(
    () => foundEvent && isEventRelatedType(foundEvent.eventType),
    [foundEvent]
  );

  const nonInitialText = useMemo(() => {
    const nonInitialTexts = massTexts.filter(
      (massText) => campaignItem.initialTextId !== massText.id
    );
    return nonInitialTexts.length ? nonInitialTexts[0] : null;
  }, [massTexts, campaignItem.initialTextId]);

  const renderTimelineItem = useCallback(
    (
      leftComp: ReactNode,
      title: string,
      subtext: string | undefined,
      foundEvent: Event | undefined
    ) => (
      <StandardBorderedContainer containerStyles={styles.timelineItemContainer}>
        <div className="AlignedRow" style={{ gap: 7 }}>
          {leftComp}
          <div className="ColumnNormal">
            <span className="OneLineText" style={styles.timelineItemHeader}>
              {title}
            </span>
            {subtext ? (
              <span
                className="OneLineText"
                style={{ fontSize: 10, color: Colors.GRAY1 }}
              >
                {truncateString(subtext, 40, "...")}
              </span>
            ) : null}
            {foundEvent && foundEvent.start && eventRelatedAutomation ? (
              <span
                className="OneLineText"
                style={{ fontSize: 10, color: Colors.GRAY1 }}
              >
                {formatDate(new Date(foundEvent.start), false, false, true)}
              </span>
            ) : null}
          </div>
        </div>
      </StandardBorderedContainer>
    ),
    [
      eventRelatedAutomation,
      styles.timelineItemContainer,
      styles.timelineItemHeader,
    ]
  );

  const renderHorizontalLine = useMemo(
    () => (
      <div style={{ width: 22 }}>
        <HorizontalDivider />
      </div>
    ),
    []
  );

  const renderItemIcon = useCallback(
    (scheduledText: boolean, isTriggerText: boolean) => (
      <Icon
        icon={
          isTriggerText
            ? "ion:return-down-forward-outline"
            : scheduledText
              ? "ion:timer"
              : "ion:paper-plane"
        }
        height={14}
        style={{
          color: scheduledText && !isTriggerText ? Colors.BLUE5 : Colors.BLACK,
        }}
      />
    ),
    []
  );

  // Display text item if there is only one initial text in the campaign
  const renderSingleText = useMemo(() => {
    if (!nonInitialText) {
      return null;
    }
    const scheduledText =
      nonInitialText.scheduled &&
      LessThanDate(new Date().toISOString(), nonInitialText.sentAt);
    return (
      <div className="AlignedRow" style={{ gap: 4 }}>
        {renderHorizontalLine}
        {renderTimelineItem(
          renderItemIcon(
            scheduledText,
            nonInitialText.type === MassTextType.TRIGGERTEXT
          ),
          delayedTriggerAutomation
            ? "Auto Text"
            : scheduledText
              ? "Scheduled"
              : "Sent",
          isDefaultTemplateAutomations(campaignItem.id) ||
            delayedTriggerAutomation
            ? relativeSendTimeLabel(
                nonInitialText.sentAtOffset,
                delayedTriggerAutomation
              )
            : formatNumericDate(new Date(nonInitialText.sentAt)),
          undefined
        )}
      </div>
    );
  }, [
    campaignItem.id,
    delayedTriggerAutomation,
    nonInitialText,
    renderHorizontalLine,
    renderItemIcon,
    renderTimelineItem,
  ]);

  if (!campaignDetail) {
    return null;
  }

  return (
    <div className="AlignedRow" style={{ gap: 4 }}>
      {campaignItem.dmAutomationId ? (
        <StandardBorderedContainer
          containerStyles={styles.timelineItemContainer}
        >
          <div className="AlignedRow" style={{ gap: 7 }}>
            <SocialPlatformIconItem
              platform={dmAutomationPlatform ?? DMAutomationPlatform.INSTAGRAM}
              size={26}
              containerStyles={{ backgroundColor: Colors.WHITE1 }}
            />
            <div className="ColumnNormal">
              <span className="OneLineText" style={styles.timelineItemHeader}>
                Completed DM Flow
              </span>
              <span
                className="OneLineText"
                style={{ fontSize: 10, color: Colors.GRAY1 }}
              >
                {campaignItem.title}
              </span>
            </div>
          </div>
        </StandardBorderedContainer>
      ) : (
        renderTimelineItem(
          foundEvent ? (
            <EventItemImage
              item={foundEvent}
              size={29}
              containerStyles={{
                borderRadius: 4,
                backgroundColor: Colors.GRAY6,
              }}
            />
          ) : (
            <PresetAutomationTriggerIcon
              campaignTitle={campaignItem.title}
              containerStyles={{
                borderRadius: 4,
                backgroundColor: Colors.GRAY6,
              }}
            />
          ),
          displayAutomationTriggerLabel(campaignItem, foundEvent),
          foundEvent && eventRelatedAutomation ? foundEvent.title : undefined,
          foundEvent
        )
      )}
      <div className="AlignedRow" style={{ gap: 4 }}>
        {renderHorizontalLine}
        {renderTimelineItem(
          renderItemIcon(false, true),
          "Auto Text",
          "Immediate",
          undefined
        )}
      </div>
      {massTexts.length > 2 ? (
        <div className="AlignedRow" style={{ gap: 4 }}>
          {renderHorizontalLine}
          <StandardBorderedContainer
            containerStyles={{
              ...styles.timelineItemContainer,
              paddingBlock: 11,
              width: 80,
            }}
          >
            <div className="Centering">
              <span style={{ fontSize: 10 }}>
                +{massTexts.length - 1}{" "}
                {handlePluralString("Text", massTexts.length - 1)}
              </span>
            </div>
          </StandardBorderedContainer>
        </div>
      ) : (
        renderSingleText
      )}
    </div>
  );
};
